// O SEFAZ (via biblioteca de validação de XML no backend) devolve o erro de
// schema cru — nome do elemento com namespace, [facet 'pattern'] e a regex
// aceita — que só faz sentido pra quem conhece o layout da NFe. Essa função
// extrai os elementos que falharam e aponta ONDE está o problema — não diz o
// que colocar no lugar, só o campo/cadastro que precisa de correção.
const REGEX_CAMPO_INVALIDO = /element\s+(\w+):.*?The value '([^']*)' is not accepted by the pattern '([^']*)'/;

// cEANTrib normalmente replica o mesmo valor de cEAN (código de barras do
// produto) — quando os dois falham juntos com o mesmo valor, é o mesmo
// problema pro operador, então viram um único aviso.
const ALIAS_CAMPO: Record<string, string> = {
  cEANTrib: 'cEAN',
};

const NOME_AMIGAVEL_CAMPO: Record<string, string> = {
  cEAN: 'Código de barras (GTIN) do produto',
  NCM: 'NCM do produto',
  CEST: 'CEST do produto',
  CFOP: 'CFOP do item',
  xProd: 'Descrição do produto',
  cProdANP: 'Código ANP do produto (combustível) — cadastro do produto, botão "Dados de Combustível"',
};

// Item da nota, só com os campos usados pra tentar achar QUAL item tem o
// valor que a SEFAZ/validação de schema rejeitou — qualquer chamador (NFe,
// NFC-e, ...) pode passar sua própria lista de itens aqui, desde que tenha
// esses campos.
export interface IItemParaMapeamentoErro {
  descricao?: string;
  ncm?: string;
  cfop?: number | string | null;
  ean?: string;
  cst?: string;
}

// Liga o nome do elemento XML (o que vem cru na mensagem de erro) ao campo
// correspondente do item — é o que permite cruzar "NCM: valor X inválido"
// com "qual item da nota tem ncm === X".
const CAMPO_ITEM_POR_ELEMENTO: Partial<Record<string, keyof IItemParaMapeamentoErro>> = {
  cEAN: 'ean',
  cEANTrib: 'ean',
  NCM: 'ncm',
  CFOP: 'cfop',
  CST: 'cst',
  CSOSN: 'cst',
  xProd: 'descricao',
};

const descricaoOuGenerico = (item: IItemParaMapeamentoErro) => item.descricao?.trim() || 'Item sem descrição';

// Cruza o valor que falhou (ex.: um NCM/CFOP/EAN/CST específico) com os itens
// da nota — pode achar mais de um item com o mesmo valor (ex.: dois produtos
// com o mesmo NCM errado), por isso devolve uma lista.
const encontrarItensPorValor = (
  campoElemento: string,
  valor: string,
  itens?: IItemParaMapeamentoErro[],
): string[] => {
  if (!itens || itens.length === 0) return [];
  const chave = CAMPO_ITEM_POR_ELEMENTO[campoElemento];
  if (!chave) return [];

  const encontrados = itens.filter((item) => String(item[chave] ?? '').trim() === valor.trim());
  const descricoes = encontrados.map(descricaoOuGenerico);
  // dedup, preservando ordem
  return descricoes.filter((descricao, index) => descricoes.indexOf(descricao) === index);
};

const traduzirErroSchemaXml = (mensagem: string, itens?: IItemParaMapeamentoErro[]): string | null => {
  if (!mensagem.includes('Schemas validity error')) return null;

  const problemas = new Set<string>();
  for (const linha of mensagem.split('\n')) {
    const match = linha.match(REGEX_CAMPO_INVALIDO);
    if (!match) continue;
    const [, campoBruto, valor] = match;
    const campo = ALIAS_CAMPO[campoBruto] || campoBruto;
    const nomeCampo = NOME_AMIGAVEL_CAMPO[campo] || `Campo "${campo}"`;
    const itensAfetados = encontrarItensPorValor(campoBruto, valor, itens);
    const sufixoItem = itensAfetados.length > 0 ? ` — item: ${itensAfetados.join(', ')}` : '';
    problemas.add(`${nomeCampo} (valor atual: "${valor}")${sufixoItem}`);
  }
  if (problemas.size === 0) return null;

  const lista = Array.from(problemas).map((texto) => `• ${texto}`).join('\n');
  return `Não foi possível emitir a nota fiscal — cadastro do produto:\n${lista}`;
};

// --- Rejeição da SEFAZ: {"status":"210","motivo":"Rejeicao: IE do destinatario invalida"} ---
// O "motivo" já vem em português (padrão nacional da NF-e), então não precisa de tradução —
// falta só dizer PRA ONDE o operador deve ir. As regras abaixo classificam por palavra-chave
// (não é uma cópia da tabela oficial de códigos do SEFAZ, que tem centenas de entradas e varia
// por UF) cobrindo os motivos mais comuns, apontando o cadastro e o campo — sem sugerir o valor
// a colocar, só onde está o problema.
interface IRegraOrigemRejeicao {
  contemTodos: string[];
  origem: string;
  campo: string;
}

const REGRAS_ORIGEM_REJEICAO: IRegraOrigemRejeicao[] = [
  // 297: a SEFAZ não diz qual campo — quase sempre é texto de cadastro com
  // espaço sobrando/caractere especial (ver verificarTextos em
  // validarNotaFiscal.ts, que aponta exatamente qual).
  {
    contemTodos: ['assinatura', 'difere'],
    origem: 'Texto de algum cadastro usado na nota (produto, cliente ou transportadora)',
    campo: 'normalmente um espaço sobrando ou caractere especial — clique em "Verificar Nota" para ver qual cadastro e campo',
  },
  { contemTodos: ['destinatario', 'ie'], origem: 'Cadastro do Cliente/Parceiro', campo: 'Inscrição Estadual (IE) do cliente' },
  { contemTodos: ['destinatario', 'cnpj'], origem: 'Cadastro do Cliente/Parceiro', campo: 'CNPJ do cliente' },
  { contemTodos: ['destinatario', 'cpf'], origem: 'Cadastro do Cliente/Parceiro', campo: 'CPF do cliente' },
  { contemTodos: ['destinatario', 'endereco'], origem: 'Cadastro do Cliente/Parceiro', campo: 'Endereço do cliente' },
  { contemTodos: ['destinatario'], origem: 'Cadastro do Cliente/Parceiro', campo: 'Dados cadastrais do cliente' },
  { contemTodos: ['emitente', 'ie'], origem: 'Configurações da Empresa', campo: 'Inscrição Estadual (IE) da empresa' },
  { contemTodos: ['emitente', 'cnpj'], origem: 'Configurações da Empresa', campo: 'CNPJ da empresa' },
  { contemTodos: ['emitente'], origem: 'Configurações da Empresa', campo: 'Dados fiscais da empresa' },
  { contemTodos: ['certificado'], origem: 'Certificado Digital', campo: 'Certificado digital' },
  { contemTodos: ['duplicidade'], origem: 'Nota já emitida', campo: 'Número/série da nota' },
  { contemTodos: ['ncm'], origem: 'Cadastro do Produto', campo: 'NCM do produto' },
  { contemTodos: ['cest'], origem: 'Cadastro do Produto', campo: 'CEST do produto' },
  { contemTodos: ['cfop'], origem: 'Operação Fiscal', campo: 'CFOP da operação' },
  { contemTodos: ['csosn'], origem: 'Cadastro do Produto', campo: 'Tributação (CST/CSOSN) do produto' },
  { contemTodos: ['cst'], origem: 'Cadastro do Produto', campo: 'Tributação (CST/CSOSN) do produto' },
  { contemTodos: ['gtin'], origem: 'Cadastro do Produto', campo: 'Código de barras (GTIN) do produto' },
  { contemTodos: ['ean'], origem: 'Cadastro do Produto', campo: 'Código de barras (EAN/GTIN) do produto' },
  { contemTodos: ['numeracao'], origem: 'Numeração da Nota', campo: 'Numeração/série da nota' },
  { contemTodos: ['sequencia'], origem: 'Numeração da Nota', campo: 'Numeração/série da nota' },
  { contemTodos: ['data', 'emissao'], origem: 'Data de Emissão', campo: 'Data de emissão' },
  { contemTodos: ['ambiente'], origem: 'Configuração de Ambiente', campo: 'Ambiente de emissão (Produção/Homologação)' },
];

const normalizarTexto = (texto: string) =>
  texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const capitalizar = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);

// Muita rejeição item-específica da SEFAZ cita o NÚMERO do item na própria
// frase (ex.: "...do item 3", "item nº 3", "Item: 3") — 1-based, na mesma
// ordem em que os itens aparecem na nota. Só um chute best-effort: nem toda
// rejeição cita o número (por isso "tenta mapear", não garante).
const REGEX_NUMERO_ITEM = /item\s*(?:n[ºo°]?\.?\s*)?(\d+)/i;

const encontrarItemPorNumero = (
  mensagem: string,
  itens?: IItemParaMapeamentoErro[],
): string | null => {
  if (!itens || itens.length === 0) return null;
  const match = mensagem.match(REGEX_NUMERO_ITEM);
  if (!match) return null;
  const indice = Number(match[1]) - 1;
  const item = itens[indice];
  return item ? descricaoOuGenerico(item) : null;
};

const traduzirRejeicaoSefaz = (mensagem: string, itens?: IItemParaMapeamentoErro[]): string | null => {
  let payload: { status?: string; motivo?: string } | null = null;
  try {
    payload = JSON.parse(mensagem);
  } catch {
    return null;
  }
  if (!payload?.motivo) return null;

  const motivo = payload.motivo.replace(/^Rejei[cç][aã]o:\s*/i, '').trim();
  const motivoNormalizado = normalizarTexto(motivo);
  const regra = REGRAS_ORIGEM_REJEICAO.find((r) => r.contemTodos.every((palavra) => motivoNormalizado.includes(palavra)));

  const onde = regra ? `${regra.origem} — ${regra.campo}` : 'Dados da Nota Fiscal';
  const itemAfetado = encontrarItemPorNumero(motivo, itens);
  const linhaItem = itemAfetado ? `\nItem afetado: ${itemAfetado}` : '';

  return `A SEFAZ rejeitou a nota${payload.status ? ` (código ${payload.status})` : ''}.\nMotivo: ${capitalizar(motivo)}.\nOnde está o erro: ${onde}.${linhaItem}`;
};

export const traduzirErroFiscal = (mensagem: string, itens?: IItemParaMapeamentoErro[]): string => {
  if (!mensagem) return mensagem;
  return traduzirRejeicaoSefaz(mensagem, itens) || traduzirErroSchemaXml(mensagem, itens) || mensagem;
};
